import React, { useState, useEffect } from "react";
import { ArrowBack, TrendingUp, DateRange, Search } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Chart } from "react-google-charts";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    BackButton,
} from "../../components/common";

function UsagePage() {
    const history = useHistory();
    const [isLoading, setIsLoading] = useState(false);
    const [usageData, setUsageData] = useState(null);

    // Filtros de data
    const today = new Date().toISOString().split('T')[0];
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const [startDate, setStartDate] = useState(sevenDaysAgo);
    const [endDate, setEndDate] = useState(today);

    // Função para formatar data de yyyy-MM-dd para dd/MM/yyyy
    const formatDateToBR = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-');
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        fetchUsageData();
    }, []);

    const fetchUsageData = async () => {
        try {
            setIsLoading(true);
            const params = new URLSearchParams({
                startDate: startDate,
                endDate: endDate
            });

            const { data } = await fetchClient.get(`/api-key-usage?${params.toString()}`);
            setUsageData(data);
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.message || "Erro ao buscar dados de consumo");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        if (new Date(startDate) > new Date(endDate)) {
            message.error("Data inicial não pode ser maior que data final");
            return;
        }
        fetchUsageData();
    };

    // Preparar dados para o gráfico
    const prepareChartData = () => {
        if (!usageData || !usageData.usage?.dailyUsage) {
            return [["Data", "Requisições"]];
        }

        const chartData = [["Data", "Requisições"]];
        const dailyUsage = usageData.usage.dailyUsage;

        // Verificar se dailyUsage é um array
        if (Array.isArray(dailyUsage)) {
            if (dailyUsage.length === 0) {
                const start = usageData.usage.period?.start || new Date().toISOString().split('T')[0];
                chartData.push([start, 0]);
            } else {
                dailyUsage.forEach(item => {
                    const date = item.date || '';
                    const requests = Number(item.requests) || 0;
                    chartData.push([date, requests]);
                });
            }
        } else {
            // Fallback para formato objeto (caso antigo)
            const dailyUsageEntries = Object.entries(dailyUsage);
            if (dailyUsageEntries.length === 0) {
                const start = usageData.usage.period?.start || new Date().toISOString().split('T')[0];
                chartData.push([start, 0]);
            } else {
                dailyUsageEntries.forEach(([date, count]) => {
                    chartData.push([date, Number(count) || 0]);
                });
            }
        }

        return chartData;
    };

    const hasUsageData = usageData && usageData.usage && (
        Array.isArray(usageData.usage.dailyUsage)
            ? usageData.usage.dailyUsage.length > 0
            : Object.keys(usageData.usage.dailyUsage || {}).length > 0
    );

    const chartOptions = {
        title: "Consumo Diário de API",
        titleTextStyle: {
            fontSize: 18,
            bold: true,
            color: '#2c3e50'
        },
        hAxis: {
            title: "Data",
            titleTextStyle: { color: '#6b7280', fontSize: 14 },
            textStyle: { color: '#6b7280', fontSize: 12 }
        },
        vAxis: {
            title: "Número de Requisições",
            titleTextStyle: { color: '#6b7280', fontSize: 14 },
            textStyle: { color: '#6b7280', fontSize: 12 },
            minValue: 0
        },
        legend: { position: "none" },
        colors: ['#00bcd4'],
        backgroundColor: 'transparent',
        chartArea: {
            width: '85%',
            height: '70%'
        },
        curveType: 'function',
        animation: {
            startup: true,
            duration: 1000,
            easing: 'out'
        }
    };

    if (isLoading) return <Loader />;

    return (
        <Container>
            <ModernHeader>
                <HeaderLeft>
                    <BackButton color="#00bcd4" onClick={() => history.goBack()}>
                        <ArrowBack />
                    </BackButton>
                    <HeaderContent>
                        <PageTitle>
                            <TrendingUp style={{ marginRight: '12px', fontSize: '32px' }} />
                            Utilização - Consumo de API
                        </PageTitle>
                        <PageSubtitle>
                            Monitore o consumo e uso da API do Comerzzia
                        </PageSubtitle>
                    </HeaderContent>
                </HeaderLeft>
            </ModernHeader>

            <FiltersCard>
                <FilterTitle>
                    <DateRange style={{ marginRight: '8px', fontSize: '20px' }} />
                    Filtros de Período
                </FilterTitle>
                <FiltersGrid>
                    <FilterGroup>
                        <FilterLabel>Data Inicial</FilterLabel>
                        <DateInput
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <FilterLabel>Data Final</FilterLabel>
                        <DateInput
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={today}
                        />
                    </FilterGroup>
                    <FilterGroup>
                        <SearchButton onClick={handleSearch}>
                            <Search style={{ marginRight: '8px', fontSize: '20px' }} />
                            Buscar
                        </SearchButton>
                    </FilterGroup>
                </FiltersGrid>
            </FiltersCard>

            {usageData && (
                <>
                    <StatsGrid>
                        <StatCard>
                            <StatIcon color="#00bcd4">
                                <TrendingUp style={{ fontSize: '32px' }} />
                            </StatIcon>
                            <StatContent>
                                <StatLabel>Total de Requisições</StatLabel>
                                <StatValue>{usageData.usage?.totalRequests || 0}</StatValue>
                            </StatContent>
                        </StatCard>

                        <StatCard>
                            <StatIcon color="#667eea">
                                <DateRange style={{ fontSize: '32px' }} />
                            </StatIcon>
                            <StatContent>
                                <StatLabel>Período</StatLabel>
                                <StatValue style={{ fontSize: '16px' }}>
                                    {formatDateToBR(usageData.usage?.period?.start)} até {formatDateToBR(usageData.usage?.period?.end)}
                                </StatValue>
                            </StatContent>
                        </StatCard>

                        <StatCard>
                            <StatIcon color="#27ae60">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                                </svg>
                            </StatIcon>
                            <StatContent>
                                <StatLabel>API Key</StatLabel>
                                <StatValue style={{ fontSize: '16px' }}>
                                    {usageData.apiKeyName || "N/A"}
                                </StatValue>
                            </StatContent>
                        </StatCard>
                    </StatsGrid>

                    {usageData.usagePlan && (
                        <InfoCard>
                            <InfoTitle>Plano de Uso</InfoTitle>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>Nome do Plano:</InfoLabel>
                                    <InfoValue>{usageData.usagePlan.name}</InfoValue>
                                </InfoItem>
                                {usageData.usagePlan.quota && (
                                    <InfoItem>
                                        <InfoLabel>Quota Limite:</InfoLabel>
                                        <InfoValue>{usageData.usagePlan.quota.limit || "Ilimitado"}</InfoValue>
                                    </InfoItem>
                                )}
                                {usageData.usagePlan.throttle && (
                                    <>
                                        <InfoItem>
                                            <InfoLabel>Taxa Limite:</InfoLabel>
                                            <InfoValue>{usageData.usagePlan.throttle.rateLimit || "N/A"} req/s</InfoValue>
                                        </InfoItem>
                                        <InfoItem>
                                            <InfoLabel>Burst Limite:</InfoLabel>
                                            <InfoValue>{usageData.usagePlan.throttle.burstLimit || "N/A"}</InfoValue>
                                        </InfoItem>
                                    </>
                                )}
                            </InfoGrid>
                        </InfoCard>
                    )}

                    <ChartCard>
                        <ChartTitle>Gráfico de Consumo</ChartTitle>
                        <ChartWrapper>
                            {!hasUsageData ? (
                                <EmptyChartMessage>
                                    <TrendingUp style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '12px' }} />
                                    <EmptyChartTitle>Nenhum dado de consumo encontrado</EmptyChartTitle>
                                    <EmptyChartText>
                                        Não há requisições registradas no período selecionado.
                                    </EmptyChartText>
                                </EmptyChartMessage>
                            ) : (
                                <Chart
                                    chartType="LineChart"
                                    width="100%"
                                    height="400px"
                                    data={prepareChartData()}
                                    options={chartOptions}
                                />
                            )}
                        </ChartWrapper>
                    </ChartCard>
                </>
            )}
        </Container>
    );
}

export default UsagePage;

// Animações
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 24px;
  margin: 0 auto 24px;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease;

  @media (min-width: 769px) {
    margin-left: calc(50% - 600px + 2rem);
    margin-right: calc(50% - 600px + 2rem);
  }

  @media (max-width: 768px) {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const FilterTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  font-family: 'Mulish', sans-serif;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 16px;
  align-items: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  font-family: 'Mulish', sans-serif;
`;

const DateInput = styled.input`
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #00bcd4;
    box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
  }
`;

const SearchButton = styled.button`
  background: linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-family: 'Mulish', sans-serif;
  box-shadow: 0 2px 8px rgba(0, 188, 212, 0.3);
  height: 42px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin: 0 auto 24px;
  max-width: 1200px;
  animation: ${fadeIn} 0.5s ease 0.1s both;

  @media (min-width: 769px) {
    margin-left: calc(50% - 600px + 2rem);
    margin-right: calc(50% - 600px + 2rem);
  }

  @media (max-width: 768px) {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const StatCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const StatIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: ${props => props.color ? `${props.color}15` : '#f3f4f6'};
  color: ${props => props.color || '#6b7280'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 4px;
  font-family: 'Mulish', sans-serif;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  font-family: 'Mulish', sans-serif;
`;

const InfoCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 24px;
  margin: 0 auto 24px;
  max-width: 1200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease 0.2s both;

  @media (min-width: 769px) {
    margin-left: calc(50% - 600px + 2rem);
    margin-right: calc(50% - 600px + 2rem);
  }

  @media (max-width: 768px) {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  font-family: 'Mulish', sans-serif;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  font-family: 'Mulish', sans-serif;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  font-family: 'Mulish', sans-serif;
`;

const ChartCard = styled.div`
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  padding: 24px;
  margin: 0 auto 24px;
  max-width: 1200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  animation: ${fadeIn} 0.5s ease 0.3s both;

  @media (min-width: 769px) {
    margin-left: calc(50% - 600px + 2rem);
    margin-right: calc(50% - 600px + 2rem);
  }

  @media (max-width: 768px) {
    margin-left: 1rem;
    margin-right: 1rem;
  }
`;

const ChartTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0 0 20px 0;
  font-family: 'Mulish', sans-serif;
`;

const ChartWrapper = styled.div`
  width: 100%;
  min-height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const EmptyChartMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
`;

const EmptyChartTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 8px 0;
  font-family: 'Mulish', sans-serif;
`;

const EmptyChartText = styled.p`
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
  font-family: 'Mulish', sans-serif;
`;
